// Onboarding clinico / lettere di incarico (DBEK-391 epic).
// I valori delle costanti sotto rispecchiano manualmente le classi PHP equivalenti in
// decisions_backend/app/Classes/*.php (ClinicianMacroStato, ClinicianDocumentTemplateScope,
// ClinicianCandidateDocumentStatus, PaAuthorizationStatus, FiscalRegime, EmployerType,
// ClinicianPaymentStatus, PaCommunicationStatus) - non esiste in questo repo nessuna
// generazione automatica condivisa backend/frontend, vanno tenuti allineati a mano.

export const ClinicianMacroStatus = {
  WAITING: 'WAITING',
  IN_PROGRESS: 'IN_PROGRESS',
  SIGNED: 'SIGNED',
  INVOICE_RECEIVED: 'INVOICE_RECEIVED',
  PAID: 'PAID',
  EXPIRED: 'EXPIRED',
  EXCLUDED: 'EXCLUDED',
} as const;
export type ClinicianMacroStatusType = typeof ClinicianMacroStatus[keyof typeof ClinicianMacroStatus];

/**
 * Ridisegno "documenti flessibili" (feedback utente: titolo libero, illimitati,
 * "generici oppure del solo clinico"): sostituisce il vecchio ClinicianDocumentType
 * a enum fisso. Un documento e' definito dal gestore con un titolo libero + questi
 * due assi indipendenti (scope qui, requires_upload su ClinicianDocumentTemplate).
 */
export const ClinicianDocumentTemplateScope = {
  /** Un solo file condiviso, caricato una volta dal gestore, uguale per ogni candidato. */
  GENERIC: 'GENERIC',
  /** Nessun file sul template: il gestore ne carica uno diverso per ciascun candidato. */
  PER_CANDIDATE: 'PER_CANDIDATE',
} as const;

/**
 * Sostituisce il vecchio ClinicianDocumentStatus (TO_SEND/SENT/SIGNED/ANOMALY, pensato
 * per una "firma" legale) con un vocabolario generico valido sia per la sola presa
 * visione sia per la restituzione file.
 */
export const ClinicianCandidateDocumentStatus = {
  PENDING: 'PENDING',
  VIEWED: 'VIEWED',
  RETURNED: 'RETURNED',
  VERIFIED: 'VERIFIED',
  ANOMALY: 'ANOMALY',
} as const;

export const PaAuthorizationStatus = {
  NOT_REQUIRED: 'NOT_REQUIRED',
  REQUIRED_PENDING: 'REQUIRED_PENDING',
  REQUIRED_RECEIVED: 'REQUIRED_RECEIVED',
} as const;

export const FiscalRegime = {
  OCCASIONAL_SIMPLE: 'OCCASIONAL_SIMPLE',
  INPS_SEPARATE_MANAGEMENT: 'INPS_SEPARATE_MANAGEMENT',
  VAT_NUMBER: 'VAT_NUMBER',
} as const;

export const EmployerType = {
  PUBLIC: 'PUBLIC',
  PRIVATE: 'PRIVATE',
  NONE: 'NONE',
} as const;

export const ClinicianPaymentStatus = {
  TO_PAY: 'TO_PAY',
  PAID: 'PAID',
} as const;

export const PaCommunicationStatus = {
  TO_SEND: 'TO_SEND',
  SENT: 'SENT',
} as const;

/**
 * Metadati UI per il badge di macro-stato: chiave i18n + colore + icona Material, in un
 * unico punto - riusato dalla scheda clinico (DBEK-403), dalla colonna Tabella-1
 * (DBEK-409) e dal banner del wizard clinico (DBEK-405), cosi' se lo stato cambia
 * aspetto va aggiornato una sola volta.
 */
export const CLINICIAN_MACRO_STATUS_META: Record<string, { i18nKey: string; color: string; icon: string }> = {
  [ClinicianMacroStatus.WAITING]: { i18nKey: 'components.clinical-onboarding.macro-status.waiting', color: '#9e9e9e', icon: 'schedule' },
  [ClinicianMacroStatus.IN_PROGRESS]: { i18nKey: 'components.clinical-onboarding.macro-status.in-progress', color: '#2196f3', icon: 'hourglass_top' },
  // Verde (non teal): audit UI - la convenzione reale dell'app per "successo" e'
  // #00BC1D/#2e7d32 (survey-team.component.scss .status-badge.present, src/_common.scss
  // .green-color), gia' correttamente usata sotto per PAID - solo SIGNED usava il teal
  // di nerf-simulator, un pattern non condiviso da nessun'altra parte dell'app.
  [ClinicianMacroStatus.SIGNED]: { i18nKey: 'components.clinical-onboarding.macro-status.signed', color: '#2e7d32', icon: 'draw' },
  [ClinicianMacroStatus.INVOICE_RECEIVED]: { i18nKey: 'components.clinical-onboarding.macro-status.invoice-received', color: '#7b55c8', icon: 'receipt_long' },
  [ClinicianMacroStatus.PAID]: { i18nKey: 'components.clinical-onboarding.macro-status.paid', color: '#2e7d32', icon: 'check_circle' },
  [ClinicianMacroStatus.EXPIRED]: { i18nKey: 'components.clinical-onboarding.macro-status.expired', color: '#c62828', icon: 'event_busy' },
  [ClinicianMacroStatus.EXCLUDED]: { i18nKey: 'components.clinical-onboarding.macro-status.excluded', color: '#616161', icon: 'block' },
};

/**
 * Metadati UI per il badge di stato-documento (audit UI/UX, item 7): stesso schema di
 * CLINICIAN_MACRO_STATUS_META sopra, un unico punto riusato da wizard/dialog di
 * dettaglio/dashboard tramite il componente condiviso
 * app-clinical-onboarding-document-status-badge. VERIFIED verde (non teal), stessa
 * motivazione del macro-stato sopra (sostituisce il vecchio SIGNED).
 */
export const CLINICIAN_CANDIDATE_DOCUMENT_STATUS_META: Record<string, { i18nKey: string; color: string; icon: string }> = {
  [ClinicianCandidateDocumentStatus.PENDING]: { i18nKey: 'components.clinical-onboarding.document-status.pending', color: '#9e9e9e', icon: 'schedule' },
  [ClinicianCandidateDocumentStatus.VIEWED]: { i18nKey: 'components.clinical-onboarding.document-status.viewed', color: '#2e7d32', icon: 'visibility' },
  [ClinicianCandidateDocumentStatus.RETURNED]: { i18nKey: 'components.clinical-onboarding.document-status.returned', color: '#1565c0', icon: 'upload_file' },
  [ClinicianCandidateDocumentStatus.VERIFIED]: { i18nKey: 'components.clinical-onboarding.document-status.verified', color: '#2e7d32', icon: 'verified' },
  [ClinicianCandidateDocumentStatus.ANOMALY]: { i18nKey: 'components.clinical-onboarding.document-status.anomaly', color: '#c62828', icon: 'error' },
};

/**
 * Converte un valore enum backend (es. INPS_SEPARATE_MANAGEMENT) nella forma usata
 * dalle chiavi i18n dinamiche (inps-separate-management) - i valori reali usano
 * underscore, mai spazi, quindi la pipe replaceSpaces esistente nel repo (che
 * sostituisce solo spazi veri) non basta. Unico punto condiviso, riusato da
 * dashboard e dialog di dettaglio clinico invece di essere duplicato.
 */
export function clinicalOnboardingI18nKeySuffix(value: string): string | null {
  return value ? value.toLowerCase().replace(/_/g, '-') : null;
}

export interface ClinicianDashboardKpis {
  clinicians_total: number;
  documents_expected: number;
  documents_received: number;
  documents_verified_ok: number;
  paid_count: number;
  amount_paid: number;
  amount_remaining_indicative: number;
}

export interface ClinicianDashboardRow {
  candidate_id: number;
  // Note interne del gestore su questo candidato.
  notes_count: number;
  // Valorizzato solo dopo l'importazione nel team reale della survey (ridisegno
  // "candidato" - vedi decisions_backend/app/Models/Candidate.php). null finche' il
  // clinico e' ancora "solo candidato".
  team_member_id: number | null;
  imported: boolean;
  name: string;
  email: string;
  macro_status: string;
  fiscal_regime: string | null;
  gross_amount: number | null;
  // Compenso netto inserito dal gestore; net_amount_indicative e' la stima dal
  // regime fiscale, usata quando net_amount e' vuoto.
  net_amount: number | null;
  net_amount_indicative: number | null;
  // Flag della colonna/filtro "Pagato": legge lo stato del pagamento, non
  // macro_status (che con EXCLUDED, di precedenza piu' alta, non lo direbbe piu').
  is_paid: boolean;
  paid_amount: number | null;
  payment_date: string | null;
  payment_cro_reference: string | null;
  is_excluded: boolean;
  has_bollo_reminder: boolean;
  documents_received_at: string | null;
  verification_ok: boolean;
  // Bug: la colonna "Verifica" mostrava la spunta verde anche senza IBAN
  // inserito (verification_ok tratta "nessun IBAN" come "nessun problema").
  // has_iban distingue questo caso; verification_override distingue una
  // verifica automatica da una forzata manualmente dal gestore - stesso
  // criterio gia' usato in ClinicianDetailEngagement.
  has_iban: boolean;
  verification_override: boolean | null;
  pa_authorization_status: string;
  manual_participation_override: boolean;
  pa_communication_status: string | null;
  deadline_at: string | null;
}

export interface ClinicianDashboardResponse {
  kpis: ClinicianDashboardKpis;
  clinicians: ClinicianDashboardRow[];
}

/**
 * Ridisegno "documenti flessibili" (sostituisce SurveyDocumentTemplate, ancorato al
 * vecchio ClinicianDocumentType a enum fisso): un documento definito dal gestore con
 * titolo libero + due assi indipendenti invece di un tipo predefinito.
 */
export interface ClinicianDocumentTemplate {
  id: number;
  survey_id: number;
  title: string;
  scope: string;
  requires_upload: boolean;
  // Generalizza la vecchia visibilita' condizionata hardcoded del Nulla Osta.
  visible_only_if_employer_public: boolean;
  // Generalizza il vecchio automatismo hardcoded della Fattura (decisione utente).
  counts_as_invoice: boolean;
  sort_order: number;
  version: number;
  // Valorizzati solo per scope=GENERIC (il file condiviso).
  file_path?: string | null;
  file_name?: string | null;
  file_size?: number | null;
  mime_type?: string | null;
  uploaded_by_user_id?: number | null;
  is_active: boolean;
}

export interface ClinicalOnboardingConfig {
  enabled: boolean;
  contract_deadline_days?: number;
  reminder_offsets_days?: number[];
}

export interface ClinicianPaymentInfo {
  status: string;
  paid_amount: number | null;
  payment_date: string | null;
  cro_reference: string | null;
  confirmed_at: string | null;
}

export interface ClinicianDetailEngagement {
  title: string | null;
  employer_name: string | null;
  employer_type: string | null;
  fiscal_regime: string | null;
  gross_amount: number | null;
  net_amount: number | null;
  net_amount_indicative: number | null;
  pa_authorization_status: string;
  macro_status: string;
  manual_participation_override: boolean;
  manual_override_reason: string | null;
  is_paid: boolean;
  payment: ClinicianPaymentInfo | null;
  is_excluded: boolean;
  excluded_at: string | null;
  exclusion_reason: string | null;
  verification_ok: boolean;
  // Feedback utente ("permetti all'amministratore di metterlo come valido o non
  // valido a prescindere [...] override permanente"): null = nessun override
  // (verification_ok sopra riflette la verifica automatica), true/false = scelta
  // esplicita del gestore, sempre prioritaria.
  verification_override: boolean | null;
  // DBEK-413: mai il valore in chiaro (stessa scelta di getMyEngagement), solo
  // "e' stato inserito" - il form di modifica li mostra vuoti con un hint,
  // esattamente come il wizard del clinico fa gia' per se stesso.
  has_iban: boolean;
  has_tax_code: boolean;
  can_edit: boolean;
}

/**
 * DBEK-392 (ridisegno "candidato"): il pannello di dettaglio per il gestore parte
 * sempre da un Candidate, mai da un vero OrganizationSurveyTeamMember - vedi
 * ClinicalOnboardingController::getClinicianDetail(). "imported"/"team_member_id"
 * riflettono se e in quale vero team member e' gia' stato importato, coerente con
 * ClinicianDashboardRow.
 */
export interface ClinicianDetailCandidate {
  id: number;
  name: string;
  // DBEK-413: nome/cognome grezzi (oltre a "name" gia' concatenato sopra, per la
  // visualizzazione) - il form di modifica li precompila separati.
  first_name: string;
  last_name: string | null;
  email: string;
  imported: boolean;
  team_member_id: number | null;
  notes_count: number;
}

export interface ClinicianDetailResponse {
  candidate: ClinicianDetailCandidate;
  engagement: ClinicianDetailEngagement | null;
  documents: ClinicianCandidateDocument[];
}

/**
 * Ridisegno "documenti flessibili" (sostituisce ClinicianDocument, ancorato al
 * vecchio ClinicianDocumentType a enum fisso): una riga per ogni coppia
 * (candidato x template), shape condivisa tra getMyEngagement (clinico) e
 * getClinicianDetail (gestore) - vedi
 * ClinicalOnboardingController::presentCandidateDocument().
 */
export interface ClinicianCandidateDocument {
  id: number;
  template_id: number;
  title: string;
  scope: string;
  requires_upload: boolean;
  counts_as_invoice: boolean;
  status: string;
  // Niente da vedere finche' un template PER_CANDIDATE non ha ancora un file
  // caricato dal gestore per QUESTO candidato.
  can_view: boolean;
  source_file_name: string | null;
  viewed_at: string | null;
  returned_file_name: string | null;
  returned_at: string | null;
  anomaly_reason: string | null;
  is_immutable: boolean;
}

/**
 * Storico attivita' completo di un candidato (feedback utente: "tracking
 * professionale e completo... quando accede alla documentazione, prende visione di
 * un file ecc"), raggiungibile dal pannello di dettaglio aperto dalla Tabella-1
 * della dashboard - vedi ClinicalOnboardingController::getClinicianActivity().
 */
export interface ClinicianActivityEvent {
  event: string;
  created_at: string;
  actor_email: string | null;
  payload: Record<string, any> | null;
}

/**
 * Nota interna del gestore su un candidato (mai visibile al clinico) - vedi
 * ClinicalOnboardingController::presentClinicianNote(). can_edit/can_delete sono gia'
 * calcolati dal server per l'utente che guarda.
 */
export interface ClinicianNote {
  id: number;
  body: string;
  author_user_id: number | null;
  author_name: string | null;
  author_email: string | null;
  created_at: string;
  updated_at: string;
  is_edited: boolean;
  is_mine: boolean;
  can_edit: boolean;
  can_delete: boolean;
}
